import {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  exponentialDelay,
  getFetcher,
  isNetworkError,
} from '@glovo/fetcher'
import { getConfig } from '@/tools/config'

let fetcherInstance: AxiosInstance

type RetryCallback = (
  retryCount: number,
  error: AxiosError,
  requestConfig: AxiosRequestConfig<unknown>
) => void

export let authRetryHandler: RetryCallback | null = null

export const retryCondition = (error: AxiosError) => {
  if (isNetworkError(error)) {
    return true
  }
  if (authRetryHandler) {
    return error.response!.status > 400 && error.response!.status <= 599
  } else {
    return error.response!.status > 500 && error.response!.status <= 599
  }
}

const initFetcher = () => {
  const appConfig = getConfig()
  return getFetcher({
    platform: appConfig.platform,
    baseURL: appConfig.apiUrl,
    requestTTL: appConfig.requestTTL,
    appConfig: {
      name: appConfig.name,
      project: appConfig.project,
      version: appConfig.version,
      type: appConfig.appType,
      developmentState: appConfig.developmentState,
    },
    headers: {
      'Glovo-Api-Version': appConfig.apiVersion,
    },
    retriesConfig: {
      retries: 3,
      retryCondition,
      retryDelay: exponentialDelay,
      onRetry: (retryCount, error, requestConfig) => {
        if (authRetryHandler) {
          authRetryHandler(retryCount, error, requestConfig)
        }
      },
    },
  })
}

export const setRequestHeaders = (key: string, value: string | undefined | null) => {
  if (!fetcherInstance) {
    fetcherInstance = initFetcher()
  }

  fetcherInstance.interceptors.request.use(
    (config) => {
      if (value) {
        config.headers[key] = value
      } else {
        delete config.headers[key]
      }
      return config
    },
    (error) => Promise.reject(error)
  )
}

let refreshRequest: Promise<{ accessToken: string }> | null = null
export const setAuthorization = (
  { accessToken }: { accessToken: string },
  onRefresh: () => Promise<{ accessToken: string }>,
  onError: (e: Error) => Promise<void>
) => {
  if (!fetcherInstance) {
    fetcherInstance = initFetcher()
  }

  fetcherInstance.defaults.headers.common['Authorization'] = accessToken
  authRetryHandler = async (_retryCount, error) => {
    if (error.response?.status === 401) {
      try {
        if (!refreshRequest) {
          refreshRequest = onRefresh()
        }
        const { accessToken } = await refreshRequest
        fetcherInstance.defaults.headers.common['Authorization'] = accessToken
        refreshRequest = null
        // eslint-disable-next-line @glovo/custom-rules/always-forward-error
      } catch (refreshError) {
        await onError(refreshError as Error)
      }
    }
  }
}

export const removeAuthorization = () => {
  if (!fetcherInstance) {
    throw new Error('Fetcher is not initialized')
  }
  fetcherInstance.defaults.headers.common['Authorization'] = null
  authRetryHandler = null
}

// this workaround is needed as app config is not available build time
export const fetcher = new Proxy({} as AxiosInstance, {
  get(_target: AxiosInstance, prop: keyof AxiosInstance) {
    if (!fetcherInstance) {
      fetcherInstance = initFetcher()
    }
    return fetcherInstance[prop]
  },
})
